import { BaseState } from '@bimser/common';
import { LayoutItem } from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { RuleEngineLegacyEntities } from '@bimser/components/dist/BCRuleManager';
import { FormItemTypes, isNullOrUndefined, RxEventEmitter } from '@bimser/core';
import { Map } from 'immutable';
import { all, call, select } from 'redux-saga/effects';
import { runShowConfirmationAction, runShowMessageAction } from '.';
import { runValueAssaignWithEntryAction } from './runValueAssaignWithEntryAction';
import { runValueAssaignWithFormulaAction } from './runValueAssaignWithFormulaAction';
import { runValueAssaignWithSelectionAction } from './runValueAssaignWithSelectionAction';
import { getFormItems } from '../../getFormItems';

export interface IRunAction {
	actions: RuleEngineLegacyEntities.IBaseAction[];
	rule: RuleEngineLegacyEntities.IRule;
	checkType: 'preview' | 'execution';
	windowId: string;
	validate?: boolean;
}

export function* runAction(args: IRunAction) {
	const effects: Array<any> = new Array();

	const getOldFormData = function* (): any {
		const state = yield select();
		return getFormItems(state as BaseState, args.windowId);
	};

	const data: Map<string, LayoutItem> = yield call(getOldFormData);
	const form = data.find(layoutItem => layoutItem.type === FormItemTypes.EContainerTypes.Form);
	const activeView = form.properties.get('activeView');

	const runActionByIndex = (index: string) => {
		let effect = null;

		if (args.actions[index].highlightList) {
			const isComponentExistInCurrentView = args.actions[index].highlightList.every(
				(name: string) => data.has(name)
			);
			if (!isComponentExistInCurrentView) {
				return effect;
			}
		}

		switch (args.actions[index].typeName) {
			case 'ShowMessageAction': {
				effect = call(function* () {
					yield call(
						runShowMessageAction,
						args.actions[index] as RuleEngineLegacyEntities.IShowMessageAction,
						args.windowId
					);
				});
				break;
			}
			case 'ConfirmationAction': {
				effect = call(
					runShowConfirmationAction,
					args.actions[index] as RuleEngineLegacyEntities.IShowMessageAction,
					args
				);
				break;
			}
			case 'ValidationFailureAction': {
				if (!args.validate && !isNullOrUndefined(args.validate)) {
					break;
				}

				const highlightList: string[] =
					(
						args.rule.actions.find(
							_action => _action.id === args.actions[index].id
						) as RuleEngineLegacyEntities.IShowMessageAction
					)?.highlightList || [];

				highlightList.forEach(controlName => {
					const subjectId = `highlight_${args.windowId}${controlName}${activeView}`;
					const subjectKey = RxEventEmitter.createName(subjectId);
					if (RxEventEmitter.subjects[subjectKey]) {
						RxEventEmitter.emit(subjectId, {
							type: 'ACTIVATE_CONTROL_HIGHLIGHT',
							windowId: args.windowId,
							status: true,
							alertType: args.actions[index].alertType,
							messages: args.actions[index].messages
						});
					} else {
						RxEventEmitter.uncaughtSubjects[subjectKey] = {
							type: 'ACTIVATE_CONTROL_HIGHLIGHT',
							windowId: args.windowId,
							status: true,
							alertType: args.actions[index].alertType,
							messages: args.actions[index].messages
						};
					}
				});
				effect = call(function* () {
					yield call(
						runShowMessageAction,
						args.actions[index] as RuleEngineLegacyEntities.IShowMessageAction,
						args.windowId
					);
					const onSave = args.rule.triggerEvents.find(event =>
						event.includes('.client.OnBeforeSave')
					);
					if (onSave) {
						return true;
					}
				});
				break;
			}
			case 'ValueAssignActionWithEntry': {
				effect = call(
					runValueAssaignWithEntryAction,
					args.actions[index] as RuleEngineLegacyEntities.IValueAssignActionWithEntry,
					getOldFormData,
					args.windowId
				);
				break;
			}
			case 'ValueAssignActionWithFormula': {
				effect = call(
					runValueAssaignWithFormulaAction,
					args.actions[index] as RuleEngineLegacyEntities.IValueAssignActionWithFormula,
					getOldFormData,
					args.windowId
				);
				break;
			}
			case 'ValueAssignActionWithSelection': {
				effect = call(
					runValueAssaignWithSelectionAction,
					args.actions[index] as RuleEngineLegacyEntities.IValueAssignActionWithSelection,
					getOldFormData,
					args.windowId
				);
				break;
			}
			default:
				console.warn('The action type that you trying to run is not found!');
		}

		return effect;
	};

	for (const k in args.actions) {
		effects.push(runActionByIndex(k) || null);
	}

	return yield all(effects);
}
